import React from 'react';
import { useQuery } from '@apollo/client/react';
import { FormFieldsData, FormShowTemplate } from 'framework/form';
import { StakeholderPayload, StakeholderSearchResultItem, StakeholderType } from './types';
import useStakeholdersService from './useStakeholdersService';
import { omit } from 'lodash';
import { useProjectDataContext } from 'domain/shared/newProject';
import Stakeholders from './Stakeholders';
import { ShowSection } from 'framework/ui/layout';
import { EditableListFormShowTemplate, NEW_ROW_ID_PREFIX } from 'framework/list';
import { GET_PROJECT_STAKEHOLDERS } from './graphql/stakeholdersQueries';
import { Loading } from 'framework/ui/mui-builder';
import useReconcileProjectNotifications, {
  PROJECT_NOTIFICATION_SCOPE,
} from '../shared/notifications/useReconcileProjectNotifications';

type StakeholderFormItem = StakeholderType & Record<string, unknown>;

const getLastStakeholdersUpdatedAt = (stakeholders: StakeholderSearchResultItem[]): string | null => {
  const timestamps = stakeholders
    .map(item => item.updatedAt ?? item.createdAt)
    .filter(Boolean)
    .map(date => Date.parse(date))
    .filter(timestamp => Number.isFinite(timestamp));

  return timestamps.length ? new Date(Math.max(...timestamps)).toISOString() : null;
};

const StakeholdersContext = () => {
  const { stakeholdersBatch, getStakeholdersValidationSchema } = useStakeholdersService();
  const { data: projectData } = useProjectDataContext();
  const reconcileNotifications = useReconcileProjectNotifications();
  const { data, loading } = useQuery<{ baseProject: { stakeholders: StakeholderSearchResultItem[] } }>(
    GET_PROJECT_STAKEHOLDERS,
    {
      variables: { id: projectData?.id?.toString() },
      skip: !projectData?.id,
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  );

  const getDataFunc = async () => {
    const stakeholders = data?.baseProject?.stakeholders ?? [];
    return {
      stakeholders: stakeholders as StakeholderType[],
      lastStakeholdersUpdatedAt: getLastStakeholdersUpdatedAt(stakeholders),
    };
  };

  const submitStakeholders = async (values: FormFieldsData) => {
    const formValues = values as { stakeholders: StakeholderFormItem[] };
    const itemsToSubmit = formValues.stakeholders
      .filter(item => !item['_isMarkedForDeletion'])
      .map(v =>
        omit(
          {
            ...v,
            ...(v.id?.toString().startsWith(NEW_ROW_ID_PREFIX) && { id: null }),
          },
          '__typename',
        ),
      ) as StakeholderPayload[];
    const response = await stakeholdersBatch(projectData.id, itemsToSubmit);
    await reconcileNotifications(String(projectData.id), [PROJECT_NOTIFICATION_SCOPE.STAKEHOLDERS]).catch(
      () => undefined,
    );

    const savedStakeholders = response as unknown as StakeholderSearchResultItem[];
    return {
      stakeholders: response as StakeholderType[],
      lastStakeholdersUpdatedAt: getLastStakeholdersUpdatedAt(savedStakeholders),
    };
  };

  if (loading) {
    return <Loading isInitialPosition />;
  }

  return (
    <ShowSection sx={{ '&.section-details__wrapper': { p: '0px !important' } }}>
      <FormShowTemplate
        isInitialsLoadingPosition
        getDataFunc={getDataFunc}
        submitFormFunc={submitStakeholders}
        afterSubmit={{
          message: 'You are awesome! The Stakeholders list has been successfully updated.',
        }}
        getValidationSchemaFunc={getStakeholdersValidationSchema}
      >
        <EditableListFormShowTemplate
          listSource="stakeholders"
          localStorageKey={projectData?.id ? `project:${projectData.id}:stakeholders` : undefined}
        >
          <Stakeholders />
        </EditableListFormShowTemplate>
      </FormShowTemplate>
    </ShowSection>
  );
};

export default StakeholdersContext;
